import { isEmpty, isEqual, orderBy } from 'lodash'
import { useState, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { Container, Row, Col, Button } from 'reactstrap'
import { resolveAddresses } from '../../util/utils'
import LoadingSpinner from '../general/LoadingSpinner'
import FormAddressWrapper from '../Shop/DigitalProofingForms/FormAddressWrapper'
import CheckoutAddressPanel from './CheckoutAddressPanel'
import { setCustomerAddress, setBillingAddress } from './checkoutFunctions'
import NewAddressForm from './NewAddressForm'

const CheckoutAddressSelection = props => {
  const [newAddressMode, setNewAddressMode] = useState(false)
  const [manageAddresses, setManageAddresses] = useState(false)
  const [newBillingAddressMode, setNewBillingAddressMode] = useState(false)
  const customerAddressPresent = !isEmpty(props.selectedCustomerAddress)
  const billingAddressPresent = !isEmpty(props.selectedBillingAddress)
  const addressesAreEqual = isEqual(props.selectedBillingAddress, props.selectedCustomerAddress)
  const bothAddressesPresent = customerAddressPresent && billingAddressPresent
  const addressesEqualAndPresent = bothAddressesPresent && addressesAreEqual

  const orderById = (adds, order = 'desc') => {
    return orderBy(adds, 'id', order)
  }

  useEffect(() => {
    const allAddresses = [
      ...props.customer_addresses,
      ...Object.values(props.managed_customer_addresses),
      ...(props.checkout.guestAddresses || [])
    ]
    const uniqueAddresses = Object.values(
      allAddresses.reduce((acc, curr) => {
        if (curr?.id) {
          acc[curr.id] = curr
        }
        return acc
      }, {})
    )

    // If user is guest and un-checks 'use as billing', enter new billing address mode
    // only if there is not another address to choose from.
    if (
      !props.currentUser.id &&
      !props.useShippingAsBilling &&
      isEmpty(props.selectedBillingAddress) &&
      uniqueAddresses.length < 2
    ) {
      setNewBillingAddressMode(true)
    }

    // If an address gets selected, or they check the box again, exit mode
    if (!isEmpty(props.selectedBillingAddress) || props.useShippingAsBilling) {
      setNewBillingAddressMode(false)
    }
  }, [
    props.useShippingAsBilling,
    props.selectedBillingAddress,
    props.currentUser.id,
    props.customer_addresses,
    props.managed_customer_addresses,
    props.checkout.guestAddresses
  ])

  // For guest users, reset newAddressMode when shipping address is deselected
  useEffect(() => {
    if (!props.currentUser.id && isEmpty(props.selectedCustomerAddress)) {
      setNewAddressMode(false)
    }
  }, [props.selectedCustomerAddress, props.currentUser.id])

  // For guest users, reset newBillingAddressMode when billing address is deselected
  useEffect(() => {
    if (!props.currentUser.id && isEmpty(props.selectedBillingAddress)) {
      setNewBillingAddressMode(false)
    }
  }, [props.selectedBillingAddress, props.currentUser.id])

  // Hide shipping address if all items are Digital customer
  const allItemsDigitalDelivery = useMemo(
    () => props.cartItems.every(cartItem => cartItem.shippable_type === 'Digital Delivery'),
    [props.cartItems]
  )

  // after cart is loaded, check for default address
  useEffect(() => {
    if (props.checkout.cart_loading === false) {
      const defaultAddress = props.customer_addresses.find(address => address.default_shipping === true)

      if (defaultAddress) {
        const isSysMangNotAddressBook = defaultAddress.is_system_managed && !props.portal?.managed_address_book
        const isNotSysMangAddressBook =
          !defaultAddress.is_system_managed &&
          props.portal?.managed_address_book &&
          (!props.portal?.hybrid_address || props.customerGroup.disable_hybrid_shipping)

        if (!(isSysMangNotAddressBook || isNotSysMangAddressBook)) {
          // Always set the default billing address if found and conditions met
          props.setBillingAddress(defaultAddress)
          // Only set the default shipping address if NOT all items are digital
          if (!allItemsDigitalDelivery) {
            props.setCustomerAddress(defaultAddress)
          }
        }
      }
    }
  }, [
    props.checkout.cart_loading,
    props.customer_addresses,
    props.portal,
    props.customerGroup.disable_hybrid_shipping,
    allItemsDigitalDelivery,
    props.setBillingAddress,
    props.setCustomerAddress
  ])

  // watch for managed/trad address updates and consolidate them into a single array
  const shippingAddresses = useMemo(() => {
    // resolve which addresses should go to list
    const traditionalAddresses = props.customer_addresses
    const managedAddresses = Object.values(props.managed_customer_addresses)
    const guestAddresses = props.checkout.guestAddresses || []

    let addressesForList = []

    addressesForList = resolveAddresses([...traditionalAddresses, ...guestAddresses], managedAddresses, props.portal)

    const orderAddresses = orderById(addressesForList)
    return orderAddresses
  }, [props.customer_addresses, props.managed_customer_addresses, props.portal, props.checkout.guestAddresses])

  const billingAddresses = useMemo(() => {
    const traditionalAddresses = props.customer_addresses
    const managedAddresses = Object.values(props.managed_customer_addresses)
    const guestAddresses = props.checkout.guestAddresses || []
    const allBillingAddresses = [...traditionalAddresses, ...managedAddresses, ...guestAddresses]

    const uniqueBillingAddresses = Object.values(
      allBillingAddresses.reduce((acc, curr) => {
        if (curr?.id) {
          acc[curr.id] = curr
        }
        return acc
      }, {})
    )
    // billing list will receive all addresses
    const orderedBillingAddresses = orderById(uniqueBillingAddresses)
    return orderedBillingAddresses
  }, [props.customer_addresses, props.managed_customer_addresses, props.checkout.guestAddresses])

  // if all items are digital, we must always show billing because there is no shipping address
  const hideShippingAddress = allItemsDigitalDelivery

  // hide billing address if youPay is 0 and requireBillingAddressForFreeOrders is false
  const hideBillingAddress =
    (props.checkout.youPay === 0 && !props.requireBillingAddressForFreeOrders) ||
    props.useShippingAsBilling ||
    addressesEqualAndPresent

  if (props.cart_loading) return <LoadingSpinner />

  return (
    <div className="box-shadow white-background m-0">
      {hideShippingAddress ? null : (
        <>
          <div className="ckout-header">
            <h5 className="m-0">{newAddressMode === true ? 'Add New ' : 'Shipping '} Address</h5>
          </div>
          <Container>
            <Row>
              <Col>
                {/* For guest users, show form when no address is selected OR when newAddressMode is true */}
                {!props.currentUser.id && (isEmpty(props.selectedCustomerAddress) || newAddressMode) ? (
                  <NewAddressForm
                    newAddressMode={newAddressMode}
                    setNewAddressMode={setNewAddressMode}
                    afterCreateAddress={props.setCustomerAddress}
                  />
                ) : /* For registered users, show form when no addresses exist and not managed address book */
                shippingAddresses.length === 0 && props.portal.managed_address_book !== true ? (
                  <NewAddressForm
                    newAddressMode={newAddressMode}
                    setNewAddressMode={setNewAddressMode}
                    afterCreateAddress={props.setCustomerAddress}
                  />
                ) : (
                  <CheckoutAddressPanel
                    addressType="delivery"
                    addresses={shippingAddresses}
                    newAddressMode={newAddressMode}
                    manageAddresses={manageAddresses}
                  />
                )}
                {/* Don't show address management for guest users */}
                {props.currentUser.id && (
                  <FormAddressWrapper
                    manageAddresses={manageAddresses}
                    setManageAddresses={setManageAddresses}
                    addressType="delivery"
                    afterCreateAddress={props.setCustomerAddress}
                  />
                )}
              </Col>
            </Row>
          </Container>
        </>
      )}

      {hideBillingAddress ? null : (
        <div className="m-0">
          <div className="ckout-header">
            <h5 className="m-0">{newBillingAddressMode ? 'Add New' : ''} Billing Address</h5>
          </div>
          <Container className="mb-20">
            <Row>
              <Col>
                {/* Billing addresses should contain both traditional and managed addresses */}
                {/* For guest users, show form when no billing address is selected OR when newBillingAddressMode is true */}
                {!props.currentUser.id && (isEmpty(props.selectedBillingAddress) || newBillingAddressMode) ? (
                  <NewAddressForm
                    newAddressMode={newBillingAddressMode}
                    setNewAddressMode={() => setNewBillingAddressMode(false)}
                    afterCreateAddress={props.setBillingAddress}
                    isBilling={true}
                  />
                ) : /* For registered users, show form when newBillingAddressMode is true */
                newBillingAddressMode ? (
                  <NewAddressForm
                    // newAddressMode and setNewAddressMode control the button/form state inside the component
                    newAddressMode={true} // Always show form when in this mode
                    setNewAddressMode={() => setNewBillingAddressMode(false)} // Allow it to close itself
                    afterCreateAddress={props.setBillingAddress}
                    isBilling={true}
                  />
                ) : (
                  <>
                    <CheckoutAddressPanel
                      addressType="billing"
                      addresses={billingAddresses}
                      newAddressMode={newAddressMode}
                      manageAddresses={manageAddresses}
                      allItemsDigital={allItemsDigitalDelivery}
                    />
                    {/* Only show "Add New Address" button for guest users, otherwise Address Management will be available */}
                    {props.currentUser.id === null && (
                      <Row className="float-right w-100 pb-3 pl-0 pr-3">
                        <Col className="d-flex justify-content-end">
                          <Button className="mf-primary-btn" onClick={() => setNewBillingAddressMode(true)}>
                            Add New Address
                          </Button>
                        </Col>
                      </Row>
                    )}
                  </>
                )}
                {props.currentUser.id && (
                  <FormAddressWrapper
                    manageAddresses={manageAddresses}
                    setManageAddresses={setManageAddresses}
                    addressType="billing"
                    afterCreateAddress={props.setBillingAddress}
                  />
                )}
              </Col>
            </Row>
          </Container>
        </div>
      )}
    </div>
  )
}

const mapStateToProps = state => {
  return {
    portal: state.portal,
    checkout: state.checkout,
    customer_addresses: state.customer_addresses,
    managed_customer_addresses: state.managed_customer_addresses,
    selectedBillingAddress: state.checkout.selectedBillingAddress,
    selectedCustomerAddress: state.checkout.selectedCustomerAddress,
    cart_loading: state.checkout.cart_loading,
    customerGroup: state.customerGroup,
    cartItems: state.cartItems,
    currentUser: state.currentUser,
    requireBillingAddressForFreeOrders: state.currentLocation.require_billing_address_for_free_orders,
    useShippingAsBilling: state.checkout.useShippingAsBilling
  }
}

export default connect(mapStateToProps, {
  setCustomerAddress,
  setBillingAddress
})(CheckoutAddressSelection)
